import { inject, Injectable } from '@angular/core';
import { catchError, firstValueFrom, of } from 'rxjs';
import { AdminApi } from '../api';
import { AuthSessionStatus, AuthStateService } from '../auth';
import { generateCryptoKey } from '../utils/generators/crypto-key-generator';
import { KeyStoreService } from './crypto-key.store';
import { loadSession } from '../auth/session-sync/utils/session-storage.util';

const CRYPTO_SERVICE_GLOBAL_KEY = '__SYMPHONY_CRYPTO_SERVICE__';

@Injectable({ providedIn: 'root' })
export class CryptoService {
  // Optional injection - AdminApi.Client may not be available in all MFE contexts
  // When not available, crypto service will operate in degraded mode (no encryption)
  private readonly client = inject(AdminApi.Client, { optional: true });
  private readonly keyStore = inject(KeyStoreService);
  private readonly auth = inject(AuthStateService);

  private encryptionKey: CryptoKey | null = null;
  private currentUserId: number | null = null;
  private initializationPromise: Promise<void> | null = null;

  constructor() {
    (globalThis as any)[CRYPTO_SERVICE_GLOBAL_KEY] = this;
  }

  static getInstance(): CryptoService | null {
    return (globalThis as any)[CRYPTO_SERVICE_GLOBAL_KEY] ?? null;
  }

  /**
   * Initialize crypto service for the current authenticated user. Fetches salt key from backend and derives encryption
   * key using PBKDF2.
   *
   * Safe to call multiple times - subsequent calls return immediately if already initialized. Uses mutex pattern to
   * prevent race conditions during concurrent initialization.
   *
   * @throws Never throws - fails gracefully. Check isReady() to verify state.
   */
  async initialize(): Promise<void> {
    // Prevent race conditions with concurrent initialize calls
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    if (this.encryptionKey) {
      return;
    }

    this.initializationPromise = this.doInitialize();

    try {
      await this.initializationPromise;
    } finally {
      this.initializationPromise = null;
    }
  }

  private async doInitialize(): Promise<void> {
    const session = this.auth.getSession();
    let userId = Number(session.userProfile?.userId);

    if (!userId || isNaN(userId)) {
      const storedSession = loadSession();
      if (storedSession && storedSession.status === AuthSessionStatus.SignedIn) {
        userId = Number(storedSession.userProfile?.userId);
      }
    }

    if (!userId || isNaN(userId)) {
      return;
    }

    this.currentUserId = userId;

    try {
      // Try to get cached key first
      let key = await this.keyStore.getKey(userId);

      if (key) {
        this.encryptionKey = key;
        return;
      }

      const saltKey = await this.fetchSalt(userId);
      if (!saltKey) {
        return;
      }

      // Derive encryption key using PBKDF2
      try {
        key = await generateCryptoKey(userId.toString(), saltKey);
      } catch {
        return;
      }

      // Store key in IndexedDB
      try {
        await this.keyStore.saveKey(userId, key);
      } catch {
        // Continue anyway - key is in memory, just won't be cached
      }

      this.encryptionKey = key;
    } catch {
      // Don't rethrow - fail gracefully
    }
  }

  /** Fetch salt key from backend. */
  private async fetchSalt(userId: number): Promise<string | null> {
    if (!this.client) {
      return null;
    }

    try {
      const saltKey = await firstValueFrom(this.client.saltKey(userId).pipe(catchError(() => of(null))));
      return saltKey;
    } catch {
      return null;
    }
  }

  /** Check if crypto service is ready (has valid encryption key). */
  isReady(): boolean {
    return this.encryptionKey !== null;
  }

  /**
   * Get raw key bytes for encryption middleware. Returns null if not initialized. Loads from IndexedDB if not in memory
   * (handles cross-MFE scenarios).
   */
  async getKeyBytes(): Promise<Uint8Array | null> {
    // If key is in memory, use it directly
    if (this.encryptionKey) {
      try {
        const keyBytes = await crypto.subtle.exportKey('raw', this.encryptionKey);
        return new Uint8Array(keyBytes);
      } catch {
        return null;
      }
    }

    // Key not in memory — try to load from IndexedDB (handles cross-MFE bundle scenarios)
    const session = this.auth.getSession();
    if (session.status !== AuthSessionStatus.SignedIn) {
      return null;
    }

    const userId = Number(session.userProfile?.userId);
    if (!userId || isNaN(userId)) {
      return null;
    }

    try {
      const cachedKey = await this.keyStore.getKey(userId);
      if (cachedKey) {
        // Store in memory for future calls
        this.encryptionKey = cachedKey;
        this.currentUserId = userId;
        const keyBytes = await crypto.subtle.exportKey('raw', cachedKey);
        return new Uint8Array(keyBytes);
      }
    } catch {
      // Failed to load from IndexedDB
    }

    return null;
  }

  /**
   * Clear in-memory crypto keys and reset session state.
   *
   * By default, stored keys in KeyStoreService are retained across logout so NPs can resume and complete offline
   * assessments without requiring an online salt fetch. Pass `purgeStoredKey: true` only for full device data wipes or
   * account purges.
   *
   * @param purgeStoredKey - Whether to also delete the persistent key from IndexedDB (default: false)
   * @returns Promise that resolves when cleanup is complete
   */
  async clearKeys(purgeStoredKey = false): Promise<void> {
    const userId = this.currentUserId;

    try {
      // Clear in-memory key from JS heap
      this.encryptionKey = null;

      // Only delete stored key from IndexedDB if explicitly requested
      if (purgeStoredKey && userId) {
        await this.keyStore.deleteKey(userId);
      }

      this.currentUserId = null;
    } catch {
      // Still clear in-memory state even if storage clear failed
      this.encryptionKey = null;
      this.currentUserId = null;
    }
  }

  /** Clear all crypto databases (both key store and encrypted data). Use this for complete cleanup on logout. */
  async clearAllDatabases(): Promise<void> {
    try {
      await this.clearKeys();

      // Delete the entire key store database
      await this.keyStore.delete();
    } catch {
      // Fail silently
    }
  }
}
